<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'

/**
 * Props
 */
type Props = {
  size?: 'medium' | 'large'
  color?: 'primary' | 'secondary' | 'ui-text-gray-1'
  variant?: 'flat' | 'outlined' | 'text'
  disabled?: boolean
  readonly?: boolean
  fullWidth?: boolean
  loading?: boolean
  to?: RouteLocationRaw
  unsetTextTransform?: boolean // v-btn の text-transform を解除するかどうか
}

type Emits = {
  click: [event: any]
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  color: 'primary',
  variant: 'flat',
  disabled: false,
  readonly: false,
  fullWidth: false,
  loading: false,
  to: undefined,
  unsetTextTransform: undefined,
})
const emit = defineEmits<Emits>()

const onClick = (event: any): void => {
  if (props.readonly || props.disabled) {
    return
  }
  emit('click', event)
}
</script>

<template>
  <v-btn
    :color="props.disabled ? 'secondary-darken-3' : color"
    :variant="props.variant"
    rounded="pill"
    :class="[
      props.size,
      {
        'unset-text-transform': props.unsetTextTransform,
      },
    ]"
    class="a-button"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :block="props.fullWidth"
    :loading="props.loading"
    :to="props.to"
    @click="onClick"
  >
    <slot />
  </v-btn>
</template>

<style lang="scss" scoped>
.a-button {
  font-weight: 700;
  background-color: rgb(var(--v-theme-ui-text-white));

  &.large {
    height: 40px;
    font-size: 14px;
  }

  &.medium {
    height: 25px;
    font-size: 12px;
  }

  :deep {
    &:hover {
      opacity: 0.7;

      .v-btn__overlay {
        opacity: 0;
      }
    }

    &.v-btn--variant-outlined {
      &.text-primary {
        &:hover {
          border-color: rgb(var(--v-theme-ui-graph-blue-7));

          .v-btn__overlay {
            color: rgb(var(--v-theme-ui-text-white));
          }
        }
      }

      &.text-secondary {
        &:hover {
          border-color: rgb(var(--v-theme-ui-gray3));

          .v-btn__overlay {
            color: rgb(var(--v-theme-ui-text-white));
          }
        }
      }
    }

    &.v-btn--variant-text {
      padding: 8px 12px;
      font-weight: 500;

      .v-btn__content {
        font-size: 14px;
      }

      .v-ripple__container {
        display:none !important;
      }

      &:hover {
        opacity: 0.8;
      }
    }

    .v-btn__content {
      line-height: 100%;
      text-transform: none;
    }

    .v-progress-circular {
      width: 16px !important;
      height: 16px !important;
    }
  }
}

.text-secondary {
  color: rgb(var(--v-theme-ui-text-gray-1)) !important;
}

.unset-text-transform {
  text-transform: unset;
}
</style>
